import type { CSSProperties } from 'react';

import type { ContentTabStripStyle } from './view-models/contentTabStripStyle';

/**
 * Shared layout className for a single tab in the strip — each tab takes an
 * equal share of the row (`flex-1`). 1:1 with legacy `Tab.tsx:214-227`. The
 * visitor link and the editor button apply the same class so they're pixel
 * identical; only the element (`<a>` vs `<button>`) and interaction differ.
 */
export const contentTabItemClassName =
  'relative flex flex-1 items-center justify-center whitespace-nowrap px-2 py-2.5 text-center transition';

/**
 * Pure: resolved strip style + active flag → the per-item inline style (font,
 * color, active/inactive background, and the 2px underline). The hidden-tab dim
 * is NOT baked in here — that's a path-specific visual the editor applies via
 * its own className, per the pure-component contract.
 */
export function contentTabItemStyle(params: {
  strip: ContentTabStripStyle;
  isActive: boolean;
}): CSSProperties {
  const { strip, isActive } = params;
  return {
    fontFamily: strip.labelFontFamily,
    // 1:1 with legacy `Tab.tsx` `Typography.Body` (variant `body`, `size=18`).
    // `Body` has NO `FontLocation`, so there's no per-`font_config` location
    // scaling to apply — variant `body` resolves to fontSize 18 / weight 400.
    // The previous `text-[14px] font-semibold` className was wrong on both.
    fontSize: 18,
    fontWeight: 400,
    color: strip.textColor,
    backgroundColor: isActive ? strip.activeBgColor : strip.inactiveBgColor,
    borderBottomWidth: 2,
    borderBottomStyle: 'solid',
    borderBottomColor: isActive
      ? strip.activeUnderlineColor
      : strip.inactiveUnderlineColor,
  };
}
